import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Zap, Bookmark, TrendingUp, TrendingDown } from "lucide-react";
import { BACKEND_URL, DEAL_STATUS_CFG, ROLE_COLORS } from "./networkConstants";
import { fmt, getPropertyPhoto, computeDealScore } from "./networkUtils";

// ─── Author Avatar ─────────────────────────────────────────────────────────────
export function AuthorAvatar({ picture, name, size = "md", role }) {
  const [imgError, setImgError] = useState(false);
  const sizeClasses = { sm: "w-8 h-8 text-sm", md: "w-10 h-10 text-base", lg: "w-12 h-12 text-lg" };
  const initial = (name || "A")[0].toUpperCase();
  const roleCfg = ROLE_COLORS[role] || ROLE_COLORS.default;
  if (picture && !imgError) {
    const src = picture.startsWith("http") ? picture : `${BACKEND_URL}${picture}`;
    return (
      <img src={src} alt={name} onError={() => setImgError(true)}
        className={`${sizeClasses[size]} rounded-full object-cover border-2 ${roleCfg.border} flex-shrink-0`} />
    );
  }
  return (
    <div className={`${sizeClasses[size]} rounded-full ${roleCfg.bg} border-2 ${roleCfg.border} flex items-center justify-center font-bold ${roleCfg.color} flex-shrink-0`}>
      {initial}
    </div>
  );
}

// ─── Deal Status Badge ─────────────────────────────────────────────────────────
export function DealStatusBadge({ status }) {
  const cfg = DEAL_STATUS_CFG[status] || DEAL_STATUS_CFG.live;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ─── Deal Score Badge ──────────────────────────────────────────────────────────
export function DealScoreBadge({ score }) {
  if (!score) return null;
  const cfg = score >= 90 ? { label: "Elite",   cls: "text-[#00FF88] bg-emerald-500/15 border-emerald-500/40" }
            : score >= 75 ? { label: "Strong",  cls: "text-blue-400 bg-blue-500/15 border-blue-500/40"       }
            : score >= 60 ? { label: "Average", cls: "text-yellow-400 bg-yellow-500/15 border-yellow-500/40" }
            :               { label: "Risk",    cls: "text-[#FF3B30] bg-red-500/15 border-red-500/40"         };
  return (
    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-lg border text-[10px] font-black backdrop-blur-sm ${cfg.cls}`}>
      <span className="text-xs">{score}</span>
      <span className="font-semibold text-[9px]">{cfg.label}</span>
    </div>
  );
}

// ─── Live Ticker ───────────────────────────────────────────────────────────────
export function LiveTicker({ items }) {
  if (!items?.length) return null;
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden border border-[#D4AF37]/10 bg-gradient-to-r from-[#D4AF37]/[0.04] to-transparent rounded-xl py-2 relative">
      <div className="flex items-center gap-0 animate-ticker" style={{ width: "max-content" }}>
        {doubled.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-2 px-6 text-xs">
            <span className="text-sm">{item.icon}</span>
            <span className="text-white/60 whitespace-nowrap">{item.text}</span>
            <span className="text-[#D4AF37]/20 mx-2">·</span>
          </span>
        ))}
      </div>
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#0D0D0D] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#0D0D0D] to-transparent z-10 pointer-events-none" />
    </div>
  );
}

// ─── Deal Card ─────────────────────────────────────────────────────────────────
export function DealCard({ deal, onClick, initialBookmarked = false, onBookmarkChange }) {
  const [bookmarked, setBookmarked] = useState(initialBookmarked);
  const arv      = deal.arv || 0;
  const rehab    = deal.rehab_estimate || 0;
  const purchase = deal.purchase_price || 0;
  const capital  = deal.marketplace_capital_needed || purchase || 0;
  const spread   = deal.projected_spread || 0;
  const photo    = deal.cover_photo || getPropertyPhoto(deal.property_type);

  const allIn    = purchase > 0 ? purchase + rehab : 0;
  const roi      = allIn > 0 && arv > 0 ? Math.round(((arv - allIn) / allIn) * 100) : null;
  const equity   = purchase > 0 && arv > 0 ? arv - purchase : null;
  const dealScore = deal.marketplace_score || computeDealScore(arv, purchase, rehab);

  const seed      = (deal.deal_id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const viewCount = 2 + (seed % 14);
  const saveCount = 1 + (seed % 6);
  const isNew     = deal.created_at && (Date.now() - new Date(deal.created_at).getTime()) < 72 * 60 * 60 * 1000;
  const needsFunding = capital > 0 && (deal.deal_status === "live" || deal.deal_status === "draft");

  // ATTOM AVM badge
  const avmValue = deal.attom_cache?.avm?.estimated_value;
  const avmVsAsking = avmValue && purchase ? avmValue - purchase : null;
  const avmAbove = avmVsAsking != null && avmVsAsking > 0;

  const toggleBookmark = async (e) => {
    e.stopPropagation();
    const next = !bookmarked;
    setBookmarked(next);
    try {
      if (next) {
        await axios.post(`${BACKEND_URL}/api/network/watchlist/${deal.deal_id}`, {}, { withCredentials: true });
        toast.success("Deal saved to Watchlist");
      } else {
        await axios.delete(`${BACKEND_URL}/api/network/watchlist/${deal.deal_id}`, { withCredentials: true });
        toast.success("Removed from Watchlist");
      }
      onBookmarkChange?.(deal.deal_id, next);
    } catch {
      setBookmarked(!next); // revert on error
      toast.error("Failed to update watchlist");
    }
  };

  return (
    <div onClick={() => onClick(deal)} data-testid={`deal-card-${deal.deal_id}`}
      className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl overflow-hidden cursor-pointer hover:border-[#D4AF37]/40 hover:shadow-xl hover:shadow-[#D4AF37]/[0.04] transition-all duration-300 group">

      <div className="relative h-44 overflow-hidden">
        <img src={photo} alt={deal.property_address}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/10 to-transparent" />

        <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
          <DealStatusBadge status={deal.deal_status || "live"} />
          {deal.property_type && (
            <span className="text-[9px] text-white/80 bg-black/60 backdrop-blur-sm border border-white/[0.12] px-2 py-0.5 rounded-full font-medium">
              {deal.property_type}
            </span>
          )}
          {isNew && (
            <span className="text-[9px] text-yellow-400 bg-yellow-500/20 backdrop-blur-sm border border-yellow-500/40 px-2 py-0.5 rounded-full font-bold">
              New
            </span>
          )}
        </div>

        <div className="absolute top-3 right-3 flex items-center gap-1.5">
          <DealScoreBadge score={dealScore} />
          <button onClick={toggleBookmark} data-testid={`bookmark-deal-${deal.deal_id}`}
            title={bookmarked ? "Remove from Watchlist" : "Save to Watchlist"}
            className={`w-7 h-7 rounded-lg border flex items-center justify-center backdrop-blur-sm transition-all ${
              bookmarked
                ? "bg-[#D4AF37]/20 border-[#D4AF37]/50 text-[#D4AF37]"
                : "bg-black/50 border-white/[0.12] text-white/40 opacity-0 group-hover:opacity-100"
            }`}>
            <Bookmark className={`w-3.5 h-3.5 ${bookmarked ? "fill-[#D4AF37]" : ""}`} />
          </button>
        </div>

        <div className="absolute bottom-2 left-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300">
          <span className="text-[9px] text-white/70 bg-black/60 backdrop-blur-sm rounded-full px-2 py-0.5 border border-white/[0.08]">
            {viewCount} viewing
          </span>
          <span className="text-[9px] text-white/70 bg-black/60 backdrop-blur-sm rounded-full px-2 py-0.5 border border-white/[0.08]">
            {saveCount} saves
          </span>
          {needsFunding && (
            <span className="text-[9px] text-[#D4AF37] bg-[#D4AF37]/10 backdrop-blur-sm rounded-full px-2 py-0.5 border border-[#D4AF37]/30">
              Funding
            </span>
          )}
        </div>
      </div>

      <div className="p-4">
        <p className="text-white font-semibold text-sm flex items-center gap-1.5 mb-0.5">
          <MapPin className="w-3 h-3 text-[#D4AF37] flex-shrink-0" />
          <span className="truncate">{deal.property_address}</span>
        </p>
        <p className="text-white/30 text-xs mb-2">by {deal.operator_name}</p>

        {/* AVM badge — shows when ATTOM data is cached */}
        {avmValue && (
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border mb-3 ${
            avmAbove
              ? "bg-[#00FF88]/[0.06] border-[#00FF88]/20 text-[#00FF88]"
              : "bg-[#FF3B30]/[0.06] border-[#FF3B30]/20 text-[#FF3B30]"
          }`}>
            {avmAbove
              ? <TrendingUp className="w-3 h-3 flex-shrink-0" />
              : <TrendingDown className="w-3 h-3 flex-shrink-0" />
            }
            <span className="text-[10px] font-bold">AVM {fmt(avmValue)}</span>
            {avmVsAsking != null && (
              <span className="text-[9px] opacity-70">
                ({avmAbove ? "+" : ""}{fmt(Math.abs(avmVsAsking))} vs ask)
              </span>
            )}
          </div>
        )}

        {deal.marketplace_summary && (
          <p className="text-white/40 text-xs line-clamp-2 mb-3 leading-relaxed">{deal.marketplace_summary}</p>
        )}

        <div className="grid grid-cols-3 gap-2 mb-3">
          {arv > 0 && (
            <div className="bg-emerald-500/[0.06] border border-emerald-500/[0.1] rounded-lg p-2 text-center">
              <p className="text-[9px] text-white/30 mb-0.5">ARV</p>
              <p className="text-[#00FF88] font-bold text-[13px]">{fmt(arv)}</p>
            </div>
          )}
          {capital > 0 ? (
            <div className="bg-[#D4AF37]/[0.06] border border-[#D4AF37]/[0.1] rounded-lg p-2 text-center">
              <p className="text-[9px] text-white/30 mb-0.5">Capital</p>
              <p className="text-[#D4AF37] font-bold text-[13px]">{fmt(capital)}</p>
            </div>
          ) : rehab > 0 ? (
            <div className="bg-orange-500/[0.06] border border-orange-500/[0.1] rounded-lg p-2 text-center">
              <p className="text-[9px] text-white/30 mb-0.5">Rehab</p>
              <p className="text-orange-400 font-bold text-[13px]">{fmt(rehab)}</p>
            </div>
          ) : null}
          {roi != null ? (
            <div className="bg-blue-500/[0.06] border border-blue-500/[0.1] rounded-lg p-2 text-center">
              <p className="text-[9px] text-white/30 mb-0.5">ROI</p>
              <p className={`font-bold text-[13px] ${roi >= 20 ? 'text-blue-400' : roi >= 0 ? 'text-white/60' : 'text-[#FF3B30]'}`}>{roi}%</p>
            </div>
          ) : spread !== 0 ? (
            <div className="bg-blue-500/[0.06] border border-blue-500/[0.1] rounded-lg p-2 text-center">
              <p className="text-[9px] text-white/30 mb-0.5">Spread</p>
              <p className={`font-bold text-[13px] ${spread > 0 ? 'text-blue-400' : 'text-[#FF3B30]'}`}>{fmt(Math.abs(spread))}</p>
            </div>
          ) : null}
        </div>

        {equity && equity > 0 && (
          <div className="flex items-center justify-between text-xs mb-3 px-0.5">
            <span className="text-white/30">Equity at Purchase</span>
            <span className="text-[#D4AF37] font-bold">{fmt(equity)}</span>
          </div>
        )}

        <button onClick={e => { e.stopPropagation(); onClick(deal); }}
          data-testid={`enter-deal-room-${deal.deal_id}`}
          className="w-full py-2 flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/25 rounded-lg text-[#D4AF37] text-xs font-bold hover:from-[#D4AF37]/20 hover:border-[#D4AF37]/50 hover:shadow-sm hover:shadow-[#D4AF37]/10 transition-all group/btn">
          <Zap className="w-3 h-3 group-hover/btn:scale-110 transition-transform" />
          Enter Deal Room
        </button>
      </div>
    </div>
  );
}
